import { useCallback, useEffect } from 'react';
import { getToken, onMessage } from 'firebase/messaging';
import { messaging } from '../firebase';
import { supabase } from '../supabase';
import { showToast } from '../constants';

interface PushUser { id?: string; tenantId?: string }

export function usePushNotifications(currentUser: PushUser | null | undefined) {
  // Destructure to primitives BEFORE the effect: callers pass a fresh object
  // literal every render (e.g. `{ ...user }`), so keying the effect on the
  // object re-fired the FCM token fetch on EVERY render — the network tab
  // filled with repeated `users?select=fcm_tokens` requests.
  const currentUserId = currentUser?.id;
  const currentTenantId = currentUser?.tenantId;

  /**
   * Registers this device's FCM token against the current user.
   *
   * Assumes notification permission is ALREADY granted — it never prompts, so
   * it is safe to call on mount. Call requestPermission() instead when the
   * permission may still be undecided.
   */
  const registerToken = useCallback(async () => {
    if (!currentUserId || !messaging) return;
    try {
      // Get FCM token
      const token = await getToken(messaging, {
        vapidKey: 'BMydwEYqjjcdhkjm-HO4uDlF1yaSCBWrGcgRpTr8O0WjQvrd3e-x9a6800_MOvxG1AMzR-zbgYuYzUEgoSjy6ts'
      });

      if (token) {
        const tid = currentTenantId;
        const uid = currentUserId;

        // EXCLUSIVE CLAIM (shared-device fix, 2026-10-14): FCM tokens identify
        // a DEVICE, not a user. Without this call the token stays registered
        // under the PREVIOUS user after someone else logs in on the same
        // device — pushes for that user (including other people's DM text in
        // the OS notification body) then reach this device. The RPC strips
        // the token from every OTHER users row in the tenant; our own append
        // below re-adds it for the current user.
        try {
          await supabase.rpc('chat_claim_fcm_token', { p_token: token });
        } catch (claimError) {
          // Non-fatal: on RPC failure keep the legacy behavior rather than
          // leaving the device without push coverage entirely.
          console.error('[push] Failed to claim FCM token exclusively:', claimError);
        }

        // Fetch current fcmTokens array, append new token, update
        const { data: userData } = await supabase
          .from('users')
          .select('fcm_tokens')
          .eq('tenant_id', tid)
          .eq('id', uid)
          .single();

        const currentTokens = userData?.fcm_tokens || [];
        if (!currentTokens.includes(token)) {
          await supabase
            .from('users')
            .update({ fcm_tokens: [...currentTokens, token] })
            .eq('tenant_id', tid)
            .eq('id', uid);
        }
      }
    } catch (error) {
      // DOMException is expected when notifications are blocked or FCM is unavailable in dev
      if (import.meta.env?.DEV) {
        const e = error as DOMException; // Notification API rejects with DOMException (has code/name)
        console.warn('[push] Notification permission/token unavailable in dev:', e.code || e.name);
      } else {
        console.error('Error getting push permission/token:', error);
      }
    }
  }, [currentUserId, currentTenantId]);

  /**
   * Requests notification permission and, on grant, registers the FCM token.
   *
   * MUST be called from a USER GESTURE (e.g. a click handler). iOS refuses
   * Notification.requestPermission() outside a user interaction, so the
   * previous auto-request on mount silently never granted permission on
   * iPhone — the device then had no FCM token and could never receive a
   * push (e.g. a trace-chat @-mention).
   *
   * Exposed to callers so the UI can trigger it from a real tap.
   */
  const requestPermission = useCallback(async () => {
    if (!messaging) return;
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') await registerToken();
    } catch (error) {
      if (import.meta.env?.DEV) {
        const e = error as DOMException;
        console.warn('[push] Notification permission unavailable in dev:', e.code || e.name);
      } else {
        console.error('Error requesting push permission:', error);
      }
    }
  }, [registerToken]);

  useEffect(() => {
    if (!currentUserId || !messaging) return;
    const messagingClient = messaging;

    // Only auto-register when permission is ALREADY granted. Never prompt here:
    // an automatic prompt is ignored by iOS (no user gesture) and, when it does
    // appear on desktop, it is dismissed at login and the denial is permanent.
    if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
      registerToken();
    }

    // Handle foreground messages.
    //
    // FCM routes a push to this handler ONLY when a visible client exists —
    // otherwise the service worker shows a system notification instead. So a
    // chat message arriving while the tab is focused used to be dropped
    // silently by the empty handler that lived here.
    //
    // Chat pushes deliberately write NO `notifications` row (phase-2 plan
    // §4.4: the unread badge derives from chat_conversation_members
    // .last_read_at instead), so NotificationListener cannot surface them.
    // This handler is their only foreground surface.
    const unsubscribe = onMessage(messagingClient, (payload) => {
      const data = payload.data;
      if (data?.type !== 'chat-message') return;

      // Composed from runtime payload data only (sender name + message text),
      // so no user-facing string is hardcoded here.
      const sender = data.fromUserName;
      const text = data.text || data.body || '';
      const summary = sender ? `${sender}: ${text}` : text;
      if (!summary) return;

      showToast(summary, 'info');
    });

    return () => unsubscribe();
  }, [currentUserId, currentTenantId, registerToken]);

  // Returned so a real user gesture (e.g. the notification bell) can request
  // permission. Without this, a device that never granted permission has no
  // FCM token and receives no push notifications at all.
  return { requestPermission };
}
